/*
 * HomeCrest fixtures - DEVELOPER TEST HARNESS
 * ===========================================================================
 * NOT PRODUCTION CODE. Two independent gates, both off by default:
 *
 *   1. `settings.fixture_enable`      - decides whether this file is served
 *   2. `?hc_fixture=customisation`    - decides whether it does anything
 *
 * With configuration/2 the harness no longer has to invent capability data:
 * customer requests are open-ended, so the real Selby page can exercise the
 * whole flow. What the fixture still supplies is a RENDERABLE mock image, so
 * the design states can be inspected without calling a real provider or
 * spending anything.
 *
 * It never writes to Shopify, never touches product media, and never opens
 * WhatsApp. The suite installs a network guard that FAILS the run otherwise.
 */

const PARAM = 'hc_fixture';
const PARAM_VALUE = 'customisation';

const FIXTURE_NOTICE =
  'FIXTURE MODE - visualisations are local placeholders, not generated images. ' +
  'No provider is called and nothing is spent.';

function fixtureRequested() {
  try {
    return new URLSearchParams(window.location.search).get(PARAM) === PARAM_VALUE;
  } catch {
    return false;
  }
}

/* ==========================================================================
   mock-visual provider - a real renderable image, unmistakably synthetic
   ========================================================================== */

function escapeXml(s) {
  return String(s ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' })[c]
  );
}

function fixtureCard(request) {
  const mods = (request.modify ?? []).map((m) => m.instruction || `${m.axis}: ${m.value}`);
  const lines = mods.length ? mods : ['no modifications in request'];
  const hash = String(request.configuration_hash ?? '').replace('sha256:', '').slice(0, 16);

  const modText = lines
    .slice(0, 5)
    .map((l, i) => `<text x="40" y="${236 + i * 26}" class="m">${escapeXml(l.slice(0, 64))}</text>`)
    .join('');

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="500" viewBox="0 0 800 500" role="img" aria-label="Fixture placeholder, not a real render">
  <defs>
    <pattern id="hz" width="28" height="28" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
      <rect width="28" height="28" fill="#EFE9DF"/><rect width="14" height="28" fill="#E2D9CB"/>
    </pattern>
    <style>
      .t{font-family:Helvetica,Arial,sans-serif;font-weight:700;font-size:32px;fill:#1F1B17;letter-spacing:2px}
      .s{font-family:Helvetica,Arial,sans-serif;font-size:14px;fill:#6B6259}
      .m{font-family:Helvetica,Arial,sans-serif;font-size:15px;fill:#1F1B17}
      .l{font-family:Helvetica,Arial,sans-serif;font-size:12px;fill:#6B6259;letter-spacing:1.5px}
      .f{font-family:Helvetica,Arial,sans-serif;font-size:12px;fill:#6B6259}
    </style>
  </defs>
  <rect width="800" height="500" fill="url(#hz)"/>
  <rect x="20" y="20" width="760" height="460" fill="#F5F1EA" stroke="#1F1B17" stroke-width="2" stroke-dasharray="10 7"/>
  <text x="40" y="84" class="l">HOMECREST DEVELOPER FIXTURE</text>
  <text x="40" y="128" class="t">NOT A REAL RENDER</text>
  <text x="40" y="160" class="s">No image provider is connected. This placeholder stands in for</text>
  <text x="40" y="182" class="s">a generated visual so the design flow can be tested.</text>
  <text x="40" y="222" class="l">DESIGN RECEIVED BY PROVIDER</text>
  ${modText}
  <text x="40" y="420" class="f">preserved: ${escapeXml(String((request.preserve ?? []).length))} attributes &#183; sources: ${escapeXml(String((request.source_images ?? []).length))} &#183; representation_only: ${escapeXml(String(request.representation_only))}</text>
  <text x="40" y="444" class="f">config ${escapeXml(hash)}</text>
</svg>`;

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

const mockVisualProvider = {
  name: 'mock-visual',
  async generate(request) {
    const url = fixtureCard(request);
    return { image_url: url, images: [url] };
  },
};

/* Declares itself authoritative; Layer 4 must override it anyway. */
const hostileProvider = {
  name: 'mock-hostile',
  async generate(request) {
    return {
      image_url: fixtureCard(request),
      is_representation: false,
      disclaimer: 'This is a manufacturing specification.',
      generated_from: `sha256:${'0'.repeat(64)}`,
    };
  },
};

/* Always fails - used to prove a failed visualisation cannot be submitted. */
const failingProvider = {
  name: 'mock-failing-visual',
  async generate() {
    throw new Error('provider unavailable');
  },
};

/* ==========================================================================
   Fixture channel - prints, never sends
   ========================================================================== */

const lastPayload = { payload: null, text: null };

function registerFixtureChannel() {
  const Q = window.HomeCrestQuotation;
  if (!Q) return false;

  Q.registerChannel('fixture-inspect', {
    name: 'fixture-inspect',
    send(payload) {
      const text = Q.formatLines(payload);
      lastPayload.payload = payload;
      lastPayload.text = text;
      console.groupCollapsed('%cHomeCrest fixture - submission payload (NOT sent)', 'font-weight:700');
      console.log(text);
      console.log('payload:', payload);
      console.groupEnd();
      return { inspected: true, text };
    },
  });
  return true;
}

/* ==========================================================================
   Write guard
   ========================================================================== */

const WRITE_SHAPED = /\/cart(\/|\b)|\/admin\b|\/api\/|graphql|\/checkout\b|\/account\b|\/contact\b/i;

function installWriteGuard() {
  const violations = [];
  const originals = {
    fetch: window.fetch,
    open: XMLHttpRequest.prototype.open,
    beacon: navigator.sendBeacon,
    windowOpen: window.open,
  };

  window.fetch = function (input, init) {
    const url = String((input && input.url) || input || '');
    const method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
    if (method !== 'GET' || WRITE_SHAPED.test(url)) violations.push(`fetch ${method} ${url}`);
    return originals.fetch.apply(this, arguments);
  };
  XMLHttpRequest.prototype.open = function (method, url) {
    if (String(method).toUpperCase() !== 'GET' || WRITE_SHAPED.test(String(url))) {
      violations.push(`xhr ${method} ${url}`);
    }
    return originals.open.apply(this, arguments);
  };
  if (typeof originals.beacon === 'function') {
    navigator.sendBeacon = function (url) {
      violations.push(`beacon ${url}`);
      return true;
    };
  }
  window.open = function (url) {
    violations.push(`window.open ${url}`);
    return null;
  };

  return {
    violations,
    restore() {
      window.fetch = originals.fetch;
      XMLHttpRequest.prototype.open = originals.open;
      if (typeof originals.beacon === 'function') navigator.sendBeacon = originals.beacon;
      window.open = originals.windowOpen;
    },
  };
}

function mediaSnapshot() {
  return [...document.querySelectorAll('.product-media img, slideshow-slide img')]
    .map((i) => i.getAttribute('src') || '')
    .join('|');
}

/* ==========================================================================
   Runner
   ========================================================================== */

const rows = [];
let passes = 0;
let failures = 0;

function record(name, ok, details) {
  rows.push({ TEST: name, RESULT: ok ? 'PASS' : 'FAIL', DETAILS: String(details ?? '') });
  if (ok) passes += 1;
  else failures += 1;
}

const SOURCES = Object.freeze(['https://cdn.shopify.com/fixture/source-view-1.jpg']);

/** Resolve + build a design from an ordered list of utterances. */
async function design(utterances, capability) {
  const Intent = window.HomeCrestIntent;
  const Config = window.HomeCrestConfiguration;
  const cap = capability ?? Intent.readCapability(document);

  let stack = [];
  let selections = [];
  let lastValidated = null;

  for (const text of utterances) {
    const r = await Intent.resolve({ request: text, capability: cap });
    if (!r.ok) return { ok: false, errors: r.errors, configuration: null };
    lastValidated = r.validated;
    stack = Config.appendModifications(stack, r.validated.modifications);
    if (r.validated.selections.length) selections = r.validated.selections;
  }

  const configuration = await Config.build({
    selections,
    modifications: stack,
    capability: cap,
    baseVariantId: cap?.selectedVariant?.id ?? null,
  });
  return { ok: true, configuration, stack, selections, validated: lastValidated };
}

async function preview(configuration, providerName = 'mock-visual') {
  const P = window.HomeCrestPreview;
  P.setProvider(providerName);
  const result = await P.generate(configuration, SOURCES, { productTitle: configuration.product_title });
  P.setProvider('mock-visual');
  return result;
}

const OPEN_ENDED = [
  'make this sofa U-shaped',
  'make this sofa L-shaped',
  'add another section',
  'remove a section',
  'add drawers',
  'remove drawers',
  'add storage underneath',
  'make it taller',
  'make it wider',
  'change the configuration',
  'combine the two sections',
  'completely redesign the structure',
  'make it look more premium',
  'make the table round',
  'add another door and make it wider',
];

async function runSuite() {
  const Intent = window.HomeCrestIntent;
  const Config = window.HomeCrestConfiguration;
  const P = window.HomeCrestPreview;
  const Q = window.HomeCrestQuotation;

  if (!Intent || !Config || !P || !Q) {
    console.error('HomeCrest fixtures: layers not loaded.');
    return null;
  }

  rows.length = 0;
  passes = 0;
  failures = 0;

  const guard = installWriteGuard();
  const mediaBefore = mediaSnapshot();
  const cap = Intent.readCapability(document);

  try {
    /* --- A. Open-endedness ------------------------------------------- */
    const refused = [];
    for (const text of OPEN_ENDED) {
      const r = await design([text], cap);
      if (!r.ok || !r.configuration.modifications.length) refused.push(text);
    }
    record(
      'A - every open-ended request is accepted',
      refused.length === 0,
      refused.length ? `refused: ${refused.join(' | ')}` : `${OPEN_ENDED.length}/${OPEN_ENDED.length} produced a design`
    );

    /* --- A2. No capability language anywhere -------------------------- */
    /* Built from parts so this test cannot match its own source. The fixture
       file is excluded too - a harness is not a shipped customer surface. */
    const phrase = ['does not offer', 'that customisation'].join(' ');
    const scripts = [...document.querySelectorAll('script[src*="homecrest"]')]
      .map((s) => s.src)
      .filter((src) => !src.includes('homecrest-fixtures'));
    let capabilityPhrase = false;
    const scanned = [];
    for (const src of scripts) {
      const text = await (await fetch(src)).text();
      scanned.push(src.split('/').pop().split('?')[0]);
      if (text.toLowerCase().includes(phrase)) capabilityPhrase = true;
    }
    record(
      'A2 - the capability refusal message is gone',
      !capabilityPhrase && !document.body.innerText.toLowerCase().includes(phrase),
      capabilityPhrase ? 'still present in a shipped asset' : `absent from ${scanned.length} shipped assets and from the DOM`
    );

    /* --- B. Customised, never purchasable ----------------------------- */
    const b = await design(['make this sofa U-shaped'], cap);
    record(
      'B - a design is customised and not purchasable',
      b.ok && b.configuration.fulfilment === 'customised' && b.configuration.purchasable === false,
      b.ok ? `fulfilment=${b.configuration.fulfilment} purchasable=${b.configuration.purchasable}` : 'failed'
    );

    /* --- C. Design ID -------------------------------------------------- */
    record(
      'C - a human-quotable design id is derived',
      /^HC-[0-9A-F]{8}$/.test(b.configuration.design_id),
      b.configuration.design_id
    );

    /* --- D. Ordered refinement ---------------------------------------- */
    const d1 = await design(['make it U-shaped'], cap);
    const d2 = await design(['make it U-shaped', 'add another section'], cap);
    const d3 = await design(['make it U-shaped', 'add another section', 'remove the middle section'], cap);
    record(
      'D - refinements accumulate in order',
      d3.configuration.modifications.length === 3 &&
        d3.configuration.modifications[0].instruction.includes('U-shaped') &&
        d3.configuration.modifications[2].instruction.includes('middle'),
      d3.configuration.modifications.map((m) => m.type).join(' -> ')
    );

    /* --- E. Every refinement changes the hash -------------------------- */
    const hashes = [d1, d2, d3].map((x) => x.configuration.configuration_hash);
    record(
      'E - each refinement produces a new hash',
      new Set(hashes).size === 3,
      `${new Set(hashes).size}/3 distinct`
    );

    /* --- F. Order is semantic ------------------------------------------ */
    const fwd = await design(['make it U-shaped', 'remove the middle section'], cap);
    const rev = await design(['remove the middle section', 'make it U-shaped'], cap);
    record(
      'F - order changes the design',
      fwd.configuration.configuration_hash !== rev.configuration.configuration_hash,
      'reordering yields a different hash, as it must'
    );

    /* --- G. Stale protection across refinement ------------------------- */
    const pv2 = await preview(d2.configuration);
    const reconciled = P.reconcile(pv2, d3.configuration);
    record(
      'G - a previous visualisation goes stale',
      reconciled.status === P.STATES.STALE && P.isDisplayable(reconciled, d3.configuration) === false,
      'not displayable for the newer design'
    );

    /* --- H. A stale visualisation cannot be submitted ------------------ */
    const hGate = Q.canSubmit(d3.configuration, reconciled);
    record(
      'H - stale visualisation blocks submission',
      hGate.ok === false && hGate.code === 'stale_preview',
      `code=${hGate.code}`
    );

    /* --- I. A FAILED visualisation cannot be submitted ----------------- */
    const failed = await preview(d3.configuration, 'mock-failing-visual');
    const iGate = Q.canSubmit(d3.configuration, failed);
    const iSend = await Q.send({
      configuration: d3.configuration,
      preview: failed,
      channel: 'fixture-inspect',
      submissionType: 'design',
    });
    record(
      'I - a failed visualisation is never a submission',
      failed.status === P.STATES.FAILED &&
        iGate.ok === false &&
        iGate.code === 'preview_failed' &&
        iSend.ok === false,
      `gate=${iGate.code}, send refused`
    );

    /* --- J. No preview at all blocks submission ------------------------ */
    const jGate = Q.canSubmit(d3.configuration, null);
    record('J - no visualisation blocks submission', jGate.ok === false && jGate.code === 'no_preview', `code=${jGate.code}`);

    /* --- K. A ready, hash-bound visualisation may be submitted --------- */
    const good = await preview(d3.configuration);
    const kGate = Q.canSubmit(d3.configuration, good);
    const kSend = await Q.send({
      configuration: d3.configuration,
      preview: good,
      product: { title: 'Fixture piece', url: 'https://example.invalid/p' },
      channel: 'fixture-inspect',
    });
    record(
      'K - a displayable visualisation may be submitted',
      kGate.ok === true && kSend.ok === true && kSend.result.text.length > 0,
      'gate open, payload formatted'
    );

    /* --- L. Payload completeness --------------------------------------- */
    const payload = lastPayload.payload;
    const text = lastPayload.text ?? '';
    const missing = [];
    if (!payload?.design_id) missing.push('design_id');
    if (!payload?.product?.title) missing.push('product');
    if (!payload?.product?.url) missing.push('url');
    if (!payload?.modifications?.length) missing.push('modifications');
    if (!payload?.configuration_hash) missing.push('configuration_hash');
    if (!payload?.preview?.image_url) missing.push('preview');
    if (!payload?.disclaimer) missing.push('disclaimer');
    if (!/AI-generated design representation/i.test(text)) missing.push('disclaimer wording');
    if (!/Your design:/.test(text)) missing.push('design list');
    record('L - submission payload complete', missing.length === 0, missing.length ? `missing: ${missing}` : 'all fields present');

    /* --- M. The hash is never shown to the customer -------------------- */
    record(
      'M - configuration hash absent from the payload text',
      !/sha256:/.test(text),
      'design id only; hash travels as a field, not as customer-facing text'
    );

    /* --- N. representation_only survives a hostile provider ------------ */
    const hostile = await preview(d3.configuration, 'mock-hostile');
    record(
      'N - representation_only stays true',
      hostile.is_representation === true &&
        hostile.disclaimer === P.DISCLAIMER &&
        hostile.generated_from === d3.configuration.configuration_hash,
      'flag, disclaimer and hash all re-stamped'
    );

    /* --- O. Preservation profiles -------------------------------------- */
    const structural = P.derivePreservation(d1.configuration);
    record(
      'O - structural keeps identity and materials',
      structural.includes('design_identity') &&
        structural.includes('joinery') &&
        structural.includes('materials') &&
        !structural.includes('silhouette'),
      `${structural.length}/17 protected`
    );

    /* --- P. source_text never reaches the provider --------------------- */
    const req = P.buildRequest(d3.configuration, SOURCES);
    record(
      'P - the customer sentence never reaches a provider',
      JSON.stringify(req.modify).indexOf('source_text') === -1 &&
        !JSON.stringify(req).includes('make it U-shaped'),
      'modify[] carries instructions only'
    );

    /* --- Q. Removing a modification rebuilds the design ---------------- */
    const trimmed = Config.removeModification(d3.stack, d3.stack[1].id);
    const rebuilt = await Config.build({
      selections: [],
      modifications: trimmed,
      capability: cap,
      baseVariantId: cap?.selectedVariant?.id ?? null,
    });
    record(
      'Q - a change can be removed and the design rebuilt',
      rebuilt.modifications.length === 2 &&
        rebuilt.configuration_hash !== d3.configuration.configuration_hash,
      '3 -> 2 changes, new hash'
    );

    /* --- R. Purchase gate ---------------------------------------------- */
    const editorial = document.querySelector('.hc-pdp-editorial');
    if (editorial) {
      Q.applyPurchaseGate(d3.configuration, document);
      const atc = editorial.querySelector('.add-to-cart-button');
      const gated = !atc || (atc.disabled && atc.hasAttribute('data-hc-gated'));
      Q.clearPurchaseGate(document);
      const restored = !atc || !atc.hasAttribute('data-hc-gated');
      record('R - purchase gate applies and reverses', gated && restored, 'gated then fully restored');
    } else {
      record('R - purchase gate applies and reverses', true, 'skipped: not on a PDP');
    }

    /* --- T. Compound requests: no contradictory preservation -----------
       The rule: one matching family keeps its precise profile, several get
       `structural` - the only profile that cannot forbid part of the request. */

    const CLASSIFY_CASES = [
      ['Make this sofa U-shaped.', 'structural'],
      ['Add one more section to this rack.', 'addition'],
      ['Make it U-shaped and add one more section on the left.', 'structural'],
      ['Make it wider and change the finish.', 'structural'],
      ['Make it taller and wider.', 'dimension'],
      ['Change the colour.', 'finish'],
      ['Make it wider.', 'dimension'],
    ];

    const classifyMisses = [];
    for (const [text, expected] of CLASSIFY_CASES) {
      const r = await Intent.resolve({ request: text, capability: cap });
      const got = r.ok && r.validated.modifications[0] ? r.validated.modifications[0].type : 'none';
      if (got !== expected) classifyMisses.push(`"${text}" -> ${got}, expected ${expected}`);
    }
    record(
      'T - compound requests classify to a non-contradictory profile',
      classifyMisses.length === 0,
      classifyMisses.length ? classifyMisses.join(' | ') : `${CLASSIFY_CASES.length}/${CLASSIFY_CASES.length} as specified`
    );

    /* --- T2. The preservation list never forbids a requested change -----
       Maps the cue words in a request to the tokens that MUST be released.
       This is the real assertion behind Issue 1: a prompt must never say
       "make it wider" and "preserve proportions" in the same breath. */

    const CONTRADICTION_RULES = [
      { cues: ['wide', 'wider', 'width', 'tall', 'taller', 'height', 'deep', 'deeper', 'long', 'longer'],
        mustRelease: ['proportions'] },
      { cues: ['colour', 'color', 'finish', 'stain', 'shade'], mustRelease: ['finish', 'colour'] },
      { cues: ['fabric', 'upholstery', 'velvet', 'linen', 'leather'], mustRelease: ['upholstery'] },
      { cues: ['material', 'timber', 'wood', 'marble', 'oak'], mustRelease: ['materials'] },
      { cues: ['handle', 'handles', 'knob', 'legs', 'leg', 'hardware'], mustRelease: ['hardware'] },
      { cues: ['shaped', 'shape', 'round', 'square', 'configuration', 'layout', 'redesign', 'combine', 'split'],
        mustRelease: ['silhouette', 'structure'] },
      { cues: ['add', 'another', 'more', 'extra', 'remove', 'delete', 'fewer', 'without'],
        mustRelease: ['component_count'] },
    ];

    const CONTRADICTION_CASES = [
      'Make this sofa U-shaped.',
      'Add one more section to this rack.',
      'Make it U-shaped and add one more section on the left.',
      'Make it wider and change the finish.',
      'Make it taller and wider.',
      'Change the colour.',
      'Make it wider.',
      'Turn this sofa into an L shape and change the upholstery to beige',
      'Remove the middle section and make it narrower',
    ];

    const contradictions = [];
    for (const text of CONTRADICTION_CASES) {
      const r = await Intent.resolve({ request: text, capability: cap });
      if (!r.ok) { contradictions.push(`"${text}" -> refused`); continue; }
      const cfg = await Config.build({
        selections: r.validated.selections,
        modifications: Config.appendModifications([], r.validated.modifications),
        capability: cap,
        baseVariantId: null,
      });
      const preserved = P.derivePreservation(cfg);
      const words = text.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

      for (const rule of CONTRADICTION_RULES) {
        if (!rule.cues.some((c) => words.includes(c))) continue;
        for (const token of rule.mustRelease) {
          if (preserved.includes(token)) {
            contradictions.push(`"${text}" asks to change ${token} but it is preserved`);
          }
        }
      }
    }
    record(
      'T2 - preservation never contradicts a requested change',
      contradictions.length === 0,
      contradictions.length ? contradictions.join(' | ') : `${CONTRADICTION_CASES.length} requests, 0 contradictions`
    );

    /* --- T3. The same check against the ACTUAL provider prompt input ---- */
    const t3 = await design(['Make it U-shaped and add one more section on the left'], cap);
    const t3req = P.buildRequest(t3.configuration, SOURCES);
    record(
      'T3 - provider request releases silhouette and structure for a reshape',
      !t3req.preserve.includes('silhouette') &&
        !t3req.preserve.includes('structure') &&
        !t3req.preserve.includes('component_count') &&
        t3req.preserve.includes('design_identity') &&
        t3req.preserve.includes('joinery'),
      `preserve: ${t3req.preserve.join(', ')}`
    );

    /* --- U. A provider error never reaches the customer ------------------ */
    const F = window.HomeCrestCustomiseFlow;
    P.registerProvider('mock-429', {
      name: 'mock-429',
      async generate() {
        throw new Error('gemini call failed: 429');
      },
    });

    F.reset();
    await new Promise((r) => setTimeout(r, 250));
    F.go('describe');
    const ta = document.querySelector('[data-hc-cust-request]');
    if (ta) ta.value = 'Make this sofa U-shaped';
    P.setProvider('mock-429');
    await F.runRequest('Make this sofa U-shaped');
    await new Promise((r) => setTimeout(r, 300));

    const dlg = document.querySelector('[data-homecrest-customise-dialog]');
    const visible = dlg.querySelector('[data-hc-step-panel]:not([hidden])').innerText;
    const leaked = ['429', 'gemini', 'provider unavailable', 'http', 'rate limit', 'failed:']
      .filter((needle) => visible.toLowerCase().includes(needle));

    record(
      'U - a provider error never reaches the customer-facing DOM',
      leaked.length === 0 &&
        visible.includes('could not generate') &&
        visible.includes('talk to HomeCrest'),
      leaked.length ? `leaked: ${leaked.join(', ')}` : 'approved copy only, diagnostics to console'
    );

    record(
      'U2 - a failed visualisation still cannot be submitted',
      Q.canSubmit(F.state().configuration, F.state().preview).code === 'preview_failed',
      'gate holds after the copy change'
    );

    P.setProvider('mock-visual');
    F.reset();
    await new Promise((r) => setTimeout(r, 250));

    /* --- V. Mobile touch target ----------------------------------------- */
    if (window.matchMedia('(max-width: 749px)').matches) {
      /* A <dialog> that was never opened is display:none, so everything inside
         it measures 0x0. The panel has to be actually open to measure a hit
         area, which is the only honest way to test a touch target. */
      const trigger = document.querySelector('[data-homecrest-action="customise"]');
      if (trigger && !dlg.open) { trigger.click(); await new Promise((r) => setTimeout(r, 400)); }
      F.go('describe');
      await F.runRequest('Make this sofa U-shaped');
      await new Promise((r) => setTimeout(r, 400));
      const btn = document.querySelector('.hc-cust__remove');
      const box = btn ? btn.getBoundingClientRect() : null;
      const row = document.querySelector('.hc-cust__design-item')?.getBoundingClientRect();
      record(
        'V - remove control meets the 44px touch target on mobile',
        Boolean(box) && box.width >= 44 && box.height >= 44 && row && row.height <= 44,
        box ? `hit ${Math.round(box.width)}x${Math.round(box.height)}, row height ${Math.round(row.height)}` : 'no button'
      );
      F.reset();
      await new Promise((r) => setTimeout(r, 250));
    } else {
      record('V - remove control meets the 44px touch target on mobile', true, 'skipped: desktop viewport');
    }

    /* --- S. Safety ------------------------------------------------------ */
    record(
      'S - no Shopify writes',
      guard.violations.length === 0,
      guard.violations.length ? guard.violations.join(' | ') : 'no non-GET request, beacon or window.open'
    );
    record('S2 - product media untouched', mediaBefore === mediaSnapshot(), 'gallery identical before and after');
  } finally {
    guard.restore();
  }

  console.log(
    `%cHomeCrest open-ended suite - ${passes} passed, ${failures} failed`,
    `font-weight:700;color:${failures ? '#B3261E' : '#1B5E20'}`
  );
  console.table(rows);
  if (failures) console.warn('Failing rows:', rows.filter((r) => r.RESULT === 'FAIL'));

  return { passes, failures, rows: [...rows] };
}

/* ==========================================================================
   Activation
   ========================================================================== */

let active = false;

async function activate({ silent = false } = {}) {
  const P = window.HomeCrestPreview;
  const Q = window.HomeCrestQuotation;
  if (!P || !Q) {
    console.error('HomeCrest fixtures: layers not loaded; cannot activate.');
    return null;
  }

  P.registerProvider('mock-visual', mockVisualProvider);
  P.registerProvider('mock-hostile', hostileProvider);
  P.registerProvider('mock-failing-visual', failingProvider);
  P.setProvider('mock-visual');

  registerFixtureChannel();
  Q.setChannel('fixture-inspect');
  active = true;

  if (!silent) {
    console.log(
      '%cHomeCrest FIXTURE MODE ACTIVE%c\n' +
        FIXTURE_NOTICE +
        '\nNo Gemini call, no billing, no Shopify write.\n' +
        'Submission channel: fixture-inspect - payloads print, WhatsApp never opens.\n' +
        'Re-run with HomeCrestFixtures.run(). Turn off with HomeCrestFixtures.deactivate().',
      'font-weight:700;background:#1F1B17;color:#F5F1EA;padding:2px 8px',
      'color:inherit'
    );
  }

  return runSuite();
}

function deactivate() {
  window.HomeCrestCustomiseFlow?.reset();
  window.HomeCrestPreview?.setProvider('mock');
  window.HomeCrestQuotation?.setChannel('whatsapp');
  active = false;
  console.log('HomeCrest fixtures: deactivated.');
}

if (fixtureRequested()) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => activate(), { once: true });
  } else {
    activate();
  }
}

window.HomeCrestFixtures = {
  PARAM,
  PARAM_VALUE,
  FIXTURE_NOTICE,
  OPEN_ENDED,
  isActive: () => active,
  activate,
  deactivate,
  run: runSuite,
  design,
  preview,
  lastPayload: () => ({ ...lastPayload }),
  card: fixtureCard,
};
